import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp } from '@fortawesome/free-solid-svg-icons/faThumbsUp';
import { faThumbsDown } from '@fortawesome/free-solid-svg-icons/faThumbsDown';
import { faComment } from '@fortawesome/free-solid-svg-icons/faComment';
import { LangfuseWeb } from 'langfuse';
import ReactMarkdown from 'lib/react-markdown';
import classNames from 'classnames';
import remarkGfm from 'remark-gfm';

const langfuseWeb = new LangfuseWeb({
  publicKey: 'pk-lf-043c6a93-d63d-4140-bf3f-f5d3f8a5c753',
  baseUrl: 'https://us.cloud.langfuse.com'
});

const styles = {
  row: isUser => ({
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: isUser ? 'flex-end' : 'flex-start',
    gap: 8,
    margin: '8px 0'
  }),
  bubble: isUser => ({
    padding: '8px 12px',
    borderRadius: 16,
    borderBottomLeftRadius: isUser ? 16 : 4,
    borderBottomRightRadius: isUser ? 4 : 16,
    background: isUser ? '#0b93f6' : '#f1f1f1',
    color: isUser ? '#fff' : '#111',
    fontSize: 14,
    lineHeight: '1.3',
    boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
    wordBreak: 'break-word'
  }),
  avatar: isUser => ({
    width: 36,
    height: 36,
    borderRadius: '50%',
    flexShrink: 0,
    background: isUser ? '#0b93f6' : '#ddd',
    color: isUser ? '#fff' : '#222',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600
  }),
  meta: {
    display: 'block',
    marginTop: 6,
    fontSize: 11,
    opacity: 0.7
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
    opacity: 0.7
  },
  actionButton: {
    background: 'none',
    border: 'none',
    padding: '4px 8px',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12,
    color: '#666',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f0f0f0',
      color: '#333'
    }
  },
  actionButtonActive: {
    color: '#0b93f6',
    backgroundColor: '#e3f2fd'
  }
};

export default function ChatMessage({ message, role, text, timestamp, avatarUrl, className, onScore }) {
  // Support both direct props and message object (for LangflowContext compatibility)
  const messageData = message || { type: role, content: text, sender: role, timestamp, streaming: false };
  const isUser = messageData.sender === 'user' || messageData.type === 'user' || role === 'user';

  // State for scoring functionality
  const [score, setScore] = useState(messageData.score || null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState(messageData.feedback || '');
  const [pendingScore, setPendingScore] = useState(null);

  const avatarContent = avatarUrl ? (
    <img
      src={avatarUrl}
      alt={`${messageData.sender} avatar`}
      style={{ width: '100%', height: '100%', borderRadius: '50%' }}
    />
  ) : (
    <span>{messageData.sender === 'user' ? 'U' : 'B'}</span>
  );

  const displayText = messageData.content || text;
  const displayTimestamp = messageData.timestamp || timestamp;

  // Handle scoring
  const handleScore = scoreValue => {
    setPendingScore(scoreValue);
    setShowFeedbackModal(true);
  };

  const submitScore = async () => {
    const scoreData = {
      messageId: messageData.id,
      score: pendingScore,
      feedback: feedbackText,
      timestamp: new Date().toISOString(),
      traceId: messageData.traceId || messageData.id, // Use traceId if available
      observationId: messageData.observationId // Use observationId if available
    };

    setScore(pendingScore);
    setShowFeedbackModal(false);

    // Call the onScore callback if provided
    if (onScore) {
      onScore(scoreData);
    }

    // Submit to Langfuse
    try {
      const result = await langfuseWeb.score(scoreData);
      console.log('Score submitted to Langfuse:', result);
      if (result.success) {
        console.log('Score successfully submitted to Langfuse');
      } else {
        console.warn('Failed to submit score to Langfuse:', result.error);
      }
    } catch (error) {
      console.error('Error submitting score to Langfuse:', error);
    }
  };

  const cancelFeedback = () => {
    setShowFeedbackModal(false);
    setPendingScore(null);
    setFeedbackText(messageData.feedback || '');
  };

  return !message.streaming || displayText ? (
    <>
      <div style={styles.row(isUser)} className={classNames('vm-chat-message', className)}>
        {!isUser && <div style={styles.avatar(isUser)}>{avatarContent}</div>}

        <div style={{ maxWidth: '72%' }}>
          <div style={styles.bubble(isUser)}>
            <ReactMarkdown
              source={displayText}
              className="vm-chat-message-content"
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ ...anchorProps }) => (
                  <a
                    {...anchorProps}
                    className={classNames('vm-chat-message-link', anchorProps.className)}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                )
              }}
            />
          </div>
          {false && displayTimestamp && <span style={styles.meta}>{displayTimestamp}</span>}

          {/* Only show scoring actions for AI messages */}
          {false && !isUser && (
            <div style={styles.actions}>
              <button
                style={{
                  ...styles.actionButton,
                  ...(score === 1 ? styles.actionButtonActive : {})
                }}
                onClick={() => handleScore(1)}
                title="Thumbs up"
              >
                <FontAwesomeIcon icon={faThumbsUp} />
              </button>

              <button
                style={{
                  ...styles.actionButton,
                  ...(score === 0 ? styles.actionButtonActive : {})
                }}
                onClick={() => handleScore(0)}
                title="Thumbs down"
              >
                <FontAwesomeIcon icon={faThumbsDown} />
              </button>

              {(score !== null || feedbackText) && (
                <button
                  style={styles.actionButton}
                  onClick={() => setShowFeedbackModal(true)}
                  title="View/Edit feedback"
                >
                  <FontAwesomeIcon icon={faComment} />
                </button>
              )}
            </div>
          )}
        </div>

        {false && isUser && <div style={styles.avatar(isUser)}>{avatarContent}</div>}
      </div>

      {/* Feedback Modal */}
      <Modal isOpen={showFeedbackModal} toggle={cancelFeedback} centered>
        <ModalHeader toggle={cancelFeedback}>
          Feedback for {pendingScore === 1 ? 'Positive' : 'Negative'} Rating
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">
            You're rating this response as {pendingScore === 1 ? 'helpful' : 'not helpful'}. Please provide additional
            feedback (optional):
          </p>
          <Input
            type="textarea"
            rows={4}
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            placeholder="Enter your feedback here..."
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={submitScore}>
            Submit Rating
          </Button>
          <Button color="secondary" onClick={cancelFeedback}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  ) : null;
}

ChatMessage.propTypes = {
  message: PropTypes.object, // Message object from LangflowContext
  role: PropTypes.oneOf(['ai', 'user']), // Direct prop (legacy)
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]), // Direct prop (legacy)
  timestamp: PropTypes.string, // Direct prop (legacy)
  avatarUrl: PropTypes.string,
  className: PropTypes.string,
  onScore: PropTypes.func // Callback for when user scores a message
};
